import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { BOOK_A_DEMO_LINK } from 'constants/common';
import {
  intelligentSystemTestingMeta,
  counterDroneMeta,
  airportGroundOpsMeta,
  interceptorUsvMeta,
} from 'constants/pages';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

import HeaderImage from 'assets/resources/ailivesim-starter-packs/header-section.webp';

const commonData = {
  ...AUTHORS.AILiveSim,
  title: 'Stop Scoping. Start Simulating. The AILiveSim Starter Packs Are Here',
  publishDate: 'May 22, 2026',
  minutesOfRead: 6,
};

export const twentySecondMay2026ArticleMeta: PageMeta = {
  url: '/articles/ailivesim-starter-packs',
  title: commonData.title,
  description:
    'AILiveSim has productized high-fidelity AI simulation into Starter Packs: fixed-scope, fixed-price engagements that put a working simulation environment on your own system in weeks, not months.',
};

export const twentySecondMay2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    imageCaption: 'AILiveSim Starter Packs',
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          <em>We Just Productized High-Fidelity AI Simulation. Here&apos;s What&apos;s in the Box</em>
        </p>
        <p>
          For teams building autonomous systems, the gap between watching a simulation demo and
          running one on your own system has always been weeks or months of custom scoping. Starter
          Packs close that gap.
        </p>
      </>
    ),
    ...commonData,
  },

  {
    type: 'common',
    title: 'What Is a Starter Pack?',
    text: (
      <>
        <p>
          A Starter Pack is a productized AILiveSim engagement with a fixed scope, a fixed price, and
          a defined delivery window measured in weeks. Every pack ships with everything a customer
          needs to run high-fidelity AI simulation on a specific use case, without a custom proposal
          and without the cost of a full proof of concept.
        </p>
        <p>
          The point is to give a serious engineering team a working artefact in days rather than
          months. The customer ends up with a simulation environment preconfigured for the use case
          they care about, running on their own system, producing results they can take into an
          engineering review, a certification conversation, or a customer meeting.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'What is in Every Starter Pack?',
    text: (
      <>
        <p>
          Every Starter Pack ships with the same set of building blocks, configured for the use case
          the pack addresses:
        </p>
        <ul>
          <li>Ready-to-use configuration files for the simulation environment</li>
          <li>Preconfigured scenarios so the team can start testing on day one</li>
          <li>A simulation license key and the required plugins</li>
          <li>Credit-based usage with flexible orchestration in the cloud or on-premise</li>
          <li>Step-by-step documentation written for engineers</li>
          <li>Onboarding sessions and scheduled check-ins during the engagement window</li>
        </ul>
        <p>
          The credit-based model matters. Customers pay only for the simulation hours they actually
          use.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'The Four Starter Packs as an overview of all our capabilities',
    text: (
      <>
        <p>
          The first campaign covers four Starter Packs; each built for a distinct use case and buyer.
        </p>
        <p>
          <strong>
            <a href={intelligentSystemTestingMeta.url}>
              Pack 1: IST Starter Pack (Intelligent System Testing).
            </a>
          </strong>{' '}
          Autonomous systems fail at the edge, not in the average case, and that is exactly where
          field trials are too slow, costly, and incomplete to reach. IST speeds up testing and
          validation by 100x: it covers your operational design domain efficiently, exposes edge
          cases, and explains the failure modes behind them. Built around AILiveSim&apos;s Intelligent
          System Testing engine, the pack ships with two preconfigured maritime scenarios, auto-docking
          and obstacle detection and avoidance, for the QA leads, robotics engineers, and program
          managers who need to validate autonomous systems at a scale field trials cannot match.
        </p>
        <p>
          <strong>
            <a href={counterDroneMeta.url}>Pack 2: Counterdrone Starter Pack.</a>
          </strong>{' '}
          A ready-to-run ground-to-air scenario where engineers run their detection, aiming, and
          target-selection stack against configurable drone waves and receive a leaderboard report
          scoring threats neutralised, friendlies preserved, and waves failed.
        </p>
        <p>
          <strong>
            <a href={airportGroundOpsMeta.url}>Pack 3: Airport Ground Ops Starter Pack.</a>
          </strong>{' '}
          This pack supports the validation of perception and decision systems for ground operations
          at airports, drawing on AILiveSim&apos;s experience with digital airport twins.
        </p>
        <p>
          <strong>
            <a href={interceptorUsvMeta.url}>Pack 4: Interceptor USV Starter Pack.</a>
          </strong>{' '}
          This pack supports the training and testing of intercept behaviors, multi-sensor perception,
          and decision logic for unmanned surface vessels operating against fast-moving adversarial
          scenarios.
        </p>
        <p>All four Starter Packs are live — and there&apos;s more to come!</p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'A Closer Look at Pack 1: Intelligent System Testing',
    text: (
      <>
        <p>
          Pack 1 is the most concrete way to see what a Starter Pack feels like in practice. The
          customer journey runs in five steps:
        </p>
        <ol>
          <li>
            Start from the customer&apos;s operational design domain and validation strategy to create
            a set of base scenarios.
          </li>
          <li>Plug in the customer&apos;s own system, such as an autopilot or perception stack.</li>
          <li>
            Select the parameters to explore, including weather, environment, initial conditions,
            physical properties, or sensor defects.
          </li>
          <li>
            Let IST run. It selects the optimal parameter combinations and orchestrates the tests at
            scale on AILiveSim&apos;s Cloud Simulation Service.
          </li>
          <li>
            Receive interactive test reports showing where the system passes, where it fails, and
            which conditions triggered each result.
          </li>
        </ol>
        <p>
          The pack runs for four weeks, with thirty hours of simulation, unlimited editing time, two
          preconfigured scenarios, and an initial trial of the customer&apos;s own system. By the end,
          the customer has explored thousands of parameter combinations, identified where and why the
          system fails, and received an interactive report on the results.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'The Value Behind Every Starter Pack',
    text: (
      <>
        <p>
          Every Starter Pack inherits the same five capabilities from the AILiveSim platform
          underneath:
        </p>
        <ul>
          <li>
            <strong>Simulation at scale, not at cost.</strong> Orchestrate massive runs in the cloud
            with granular budget controls, so teams can test more while spending less.
          </li>
          <li>
            <strong>Total sensor mastery.</strong> Full-spectrum simulation including thermal, LiDAR,
            and GNSS-denied environments to ensure 24/7 operational reliability.
          </li>
          <li>
            <strong>Automate safety validation.</strong> Build a library of base scenarios and let
            AILiveSim&apos;s AI generate thousands of critical variations to find the system&apos;s
            breaking point.
          </li>
          <li>
            <strong>Eliminate the data bottleneck.</strong> Replace slow, manual data labeling with
            high-fidelity synthetic datasets tailored to the customer&apos;s specific sensor payload.
          </li>
          <li>
            <strong>De-risk deployment.</strong> Lower the cost of physical testing and provide the
            rigorous safety evidence required for regulatory and client approval.
          </li>
        </ul>
        <p>
          These are platform capabilities, consistent across every pack. What changes from pack to
          pack is which scenarios, sensors, and parameters come preconfigured.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Why This Is a Milestone for AILiveSim',
    text: (
      <>
        <p>
          Productizing high-fidelity AI simulation into something a customer can buy from a website,
          install in days to produce data, and complete the project within four weeks is not a small
          thing for this industry. It took the full team to get here, and we are proud of what they
          have built.
        </p>
        <p>
          The first set of packs is open and all four Starter Packs are live: the{' '}
          <a href={intelligentSystemTestingMeta.url}>IST</a>,{' '}
          <a href={counterDroneMeta.url}>Counterdrone</a>,{' '}
          <a href={airportGroundOpsMeta.url}>Airport Ground Ops</a>, and{' '}
          <a href={interceptorUsvMeta.url}>Interceptor USV</a> Starter Packs. Visit the AILiveSim
          website to enroll or{' '}
          <a href={BOOK_A_DEMO_LINK} target="_blank" rel="noopener noreferrer">
            get in touch
          </a>{' '}
          if you want to talk through which pack fits.
        </p>
      </>
    ),
  },
];

export const twentySecondMay2026ArticleCard: TCardProps = {
  description:
    'AILiveSim has productized high-fidelity AI simulation into Starter Packs: fixed-scope, fixed-price engagements that put a working simulation environment on your own system in weeks, not months.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: twentySecondMay2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.AISimulation,
    EHashtag.SyntheticData,
    EHashtag.AITraining,
    EHashtag.SafetyValidation,
    EHashtag.AutonomousVehicles,
  ],
};
